/**
 * Mock data for Mission Management
 */

import type { GhostTier, Mission, MissionType } from '../../types/mission';
import { MissionStatus } from '../../types/mission';
import { generateId } from './mockData';

const missionTypes: MissionType[] = [
  'QuickHit',
  'LightSurveillance',
  'FullInvestigative',
];

const missionTypeLabels: Record<MissionType, string> = {
  QuickHit: 'Quick Hit',
  LightSurveillance: 'Light Surveillance',
  FullInvestigative: 'Full Investigative',
};

const statuses: MissionStatus[] = [
  MissionStatus.PENDING,
  MissionStatus.PENDING_ADMIN_REVIEW,
  MissionStatus.SUBMITTED,
  MissionStatus.GHOST_ASSIGNED,
  MissionStatus.MISSION_IN_PROGRESS,
  MissionStatus.COMPLETED,
  MissionStatus.UNDER_REVIEW,
];

const ghostTiers: GhostTier[] = ['Junior', 'Senior', 'Elite'];

const users = [
  { id: 'USR-J5D9', name: 'John Doe' },
  { id: 'USR-K8F2', name: 'Jane Smith' },
  { id: 'USR-L1G5', name: 'Sam Wilson' },
  { id: 'USR-M4H8', name: 'Emily Brown' },
  { id: 'USR-N7I1', name: 'Chris Green' },
  { id: 'USR-P2J4', name: 'Alex Johnson' },
  { id: 'USR-Q5K7', name: 'Maria Garcia' },
  { id: 'USR-R8L0', name: 'David Lee' },
];

const generateMissionId = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = 'MSN-';
  for (let i = 0; i < 5; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// Generate 20 mock missions
const generateMockMissions = (): Mission[] => {
  const missions: Mission[] = [];
  const now = new Date();

  for (let i = 0; i < 20; i++) {
    const user = users[Math.floor(Math.random() * users.length)];
    const missionType = missionTypes[Math.floor(Math.random() * missionTypes.length)];
    const ghostTier = ghostTiers[Math.floor(Math.random() * ghostTiers.length)];
    const status = statuses[Math.floor(Math.random() * statuses.length)];

    const createdDate = new Date(now.getTime() - (i + 1) * 24 * 60 * 60 * 1000);
    const lastUpdatedDate = new Date(createdDate.getTime() + Math.random() * 12 * 60 * 60 * 1000);
    const startDate = new Date(createdDate.getTime() + 24 * 60 * 60 * 1000);
    const endDate = new Date(startDate.getTime() + 7 * 24 * 60 * 60 * 1000);
    const missionId = generateMissionId();

    missions.push({
      id: generateId(),
      code: `CODE-${missionId.substring(4)}`,
      title: `${missionTypeLabels[missionType]} Mission ${i + 1}`,
      missionType,
      category: 'General',
      ghostTier,
      status,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      createdAt: createdDate.toISOString(),
      updatedAt: lastUpdatedDate.toISOString(),
      assetId: user.id,
      assetFirstName: user.name.split(' ')[0],
      assetLastName: user.name.split(' ')[1] || '',
      assetCallSign: `Asset-${i + 1}`,
      assetEmail: `${user.name.toLowerCase().replace(' ', '.')}@example.com`,
      ghostId: [MissionStatus.PENDING, MissionStatus.PENDING_ADMIN_REVIEW, MissionStatus.SUBMITTED].includes(status) ? undefined : generateId(),
      ghostFirstName: [MissionStatus.PENDING, MissionStatus.PENDING_ADMIN_REVIEW, MissionStatus.SUBMITTED].includes(status) ? undefined : 'Ghost',
      ghostLastName: [MissionStatus.PENDING, MissionStatus.PENDING_ADMIN_REVIEW, MissionStatus.SUBMITTED].includes(status) ? undefined : `Num-${i}`,
      ghostCallSign: [MissionStatus.PENDING, MissionStatus.PENDING_ADMIN_REVIEW, MissionStatus.SUBMITTED].includes(status) ? undefined : `Ghost-${i}`,
      ghostEmail: [MissionStatus.PENDING, MissionStatus.PENDING_ADMIN_REVIEW, MissionStatus.SUBMITTED].includes(status) ? undefined : `ghost-${i}@example.com`,
    });
  }

  return missions;
};

// Pre-defined missions matching the reference image
const predefinedMissions: Mission[] = [
  {
    id: generateId(),
    code: 'CODE-84AD3',
    title: 'Quick Hit Mission 1',
    missionType: 'QuickHit',
    category: 'General',
    ghostTier: 'Junior',
    status: MissionStatus.MISSION_IN_PROGRESS,
    startDate: '2023-10-27T09:00:00Z',
    endDate: '2023-11-03T18:00:00Z',
    createdAt: '2023-10-26T10:00:00Z',
    updatedAt: '2023-10-26T14:30:00Z',
    assetId: 'USR-J5D9',
    assetFirstName: 'John',
    assetLastName: 'Doe',
    assetCallSign: 'JD-Asset',
    assetEmail: 'john.doe@example.com',
    ghostId: 'GHOST-001',
    ghostFirstName: 'Ghost',
    ghostLastName: 'One',
    ghostCallSign: 'Specter',
    ghostEmail: 'specter@example.com',
  },
  {
    id: generateId(),
    code: 'CODE-91BCE',
    title: 'Light Surveillance Mission 2',
    missionType: 'LightSurveillance',
    category: 'General',
    ghostTier: 'Senior',
    status: MissionStatus.COMPLETED,
    startDate: '2023-10-26T08:00:00Z',
    endDate: '2023-11-02T17:00:00Z',
    createdAt: '2023-10-25T09:00:00Z',
    updatedAt: '2023-10-25T18:00:00Z',
    assetId: 'USR-K8F2',
    assetFirstName: 'Jane',
    assetLastName: 'Smith',
    assetCallSign: 'JS-Asset',
    assetEmail: 'jane.smith@example.com',
    ghostId: 'GHOST-002',
    ghostFirstName: 'Ghost',
    ghostLastName: 'Two',
    ghostCallSign: 'Shadow',
    ghostEmail: 'shadow@example.com',
  },
  {
    id: generateId(),
    code: 'CODE-A2CFG',
    title: 'Full Investigative Mission 3',
    missionType: 'FullInvestigative',
    category: 'Investigation',
    ghostTier: 'Elite',
    status: MissionStatus.UNDER_REVIEW,
    startDate: '2023-10-25T08:30:00Z',
    endDate: '2023-11-08T19:00:00Z',
    createdAt: '2023-10-24T08:00:00Z',
    updatedAt: '2023-10-24T11:45:00Z',
    assetId: 'USR-L1G5',
    assetFirstName: 'Sam',
    assetLastName: 'Wilson',
    assetCallSign: 'SW-Asset',
    assetEmail: 'sam.wilson@example.com',
    ghostId: 'GHOST-003',
    ghostFirstName: 'Ghost',
    ghostLastName: 'Three',
    ghostCallSign: 'Falcon',
    ghostEmail: 'falcon@example.com',
  },
  {
    id: generateId(),
    code: 'CODE-B3DHI',
    title: 'Snap-&-Grab Mission 4',
    missionType: 'QuickHit',
    category: 'General',
    ghostTier: 'Junior',
    status: MissionStatus.UNDER_REVIEW,
    startDate: '2023-10-24T10:00:00Z',
    endDate: '2023-10-30T16:00:00Z',
    createdAt: '2023-10-23T07:00:00Z',
    updatedAt: '2023-10-23T09:15:00Z',
    assetId: 'USR-M4H8',
    assetFirstName: 'Emily',
    assetLastName: 'Brown',
    assetCallSign: 'EB-Asset',
    assetEmail: 'emily.brown@example.com',
    ghostId: 'GHOST-004',
    ghostFirstName: 'Ghost',
    ghostLastName: 'Four',
    ghostCallSign: 'Stalker',
    ghostEmail: 'stalker@example.com',
  },
  {
    id: generateId(),
    code: 'CODE-C4EJK',
    title: 'Light Surveillance Mission 5',
    missionType: 'LightSurveillance',
    category: 'General',
    ghostTier: 'Senior',
    status: MissionStatus.PENDING,
    startDate: '2023-10-23T09:00:00Z',
    endDate: '2023-10-29T17:30:00Z',
    createdAt: '2023-10-22T10:00:00Z',
    updatedAt: '2023-10-22T16:20:00Z',
    assetId: 'USR-N7I1',
    assetFirstName: 'Chris',
    assetLastName: 'Green',
    assetCallSign: 'CG-Asset',
    assetEmail: 'chris.green@example.com',
  },
];

// Combine predefined missions with generated ones
export const mockMissions: Mission[] = [
  ...predefinedMissions,
  ...generateMockMissions().slice(5),
];

export { missionTypeLabels };

